/** @file variant.remove: drops a variant flag and every facet entry that requires it. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import type { Drawable } from '@zilo/pose3d-format/types/drawable';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { CommandDef } from '../apply/commandDef';
import { puppetInverse } from '../apply/puppetInverse';
import { checkPuppet } from '../puppetDoc/checkPuppet';
import { readInput } from '../validate/readInput';

/** Removes the entries needing `variant` from `d` in place; returns their `<drawable>/<entry>` keys (old indices). */
function dropEntries(d: Drawable, variant: string): string[] {
  if ((d.kind !== 'impostor' && d.kind !== 'effect') || d.facets === undefined) return [];
  const removed = d.facets.flatMap((e, i) => (e.variants.includes(variant) ? [`${d.id}/${i}`] : []));
  d.facets = d.facets.filter((e) => !e.variants.includes(variant));
  return removed;
}

/**
 * `variant.remove { variant }` → `{ removedEntries }`: entries are listed by their keys before removal; an impostor
 * left without entries is removed (an effect just loses its facets). Inverse P.
 */
export const variantRemoveCommand: CommandDef<'variant.remove'> = {
  name: 'variant.remove',
  /** Checks the raw input (VALIDATION under the input path). */
  validate: (input, path) => ({ variant: readInput(input, path, ['variant']).str('variant') }),
  /** Applies the command to the draft and returns its result and inverse. */
  apply: (draft, input, ctx) => {
    const p = draft.puppet;
    if (!p.variants.includes(input.variant)) {
      throwError(ERROR_CODES.NOT_FOUND, { variant: input.variant }, { path: '/input/variant', hint: `variants: ${p.variants.join(', ')}` });
    }
    const inverse = puppetInverse(draft);
    p.variants = p.variants.filter((v) => v !== input.variant);
    const removedEntries = p.drawables.flatMap((d) => dropEntries(d, input.variant));
    p.drawables = p.drawables.filter((d) => d.kind !== 'impostor' || d.facets.length > 0);
    for (const d of p.drawables) if (d.kind === 'effect' && d.facets?.length === 0) delete d.facets;
    checkPuppet(draft, ctx);
    return { result: { removedEntries }, warnings: [], inverse };
  },
};
